import React, { useMemo, useRef, useEffect, Suspense } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Text, Billboard, OrbitControls } from "@react-three/drei";
import { TeleportTarget, useXR } from "@react-three/xr";
import * as THREE from "three";
import { surgeVert, surgeFrag } from "../lib/shaders";
import { GOLD } from "../lib/storm";
import { PROFILES } from "../lib/quality";

// distinct per-city block massing + landmark silhouettes
const CITY_LAYOUTS = {
  nyc: { rows: 15, hMin: 10, hMax: 28, hue: 0.6, sat: 0.06, light: 0.2, spread: 5.2, spires: 3, spireH: 40, palm: false },
  boston: { rows: 12, hMin: 5, hMax: 14, hue: 0.07, sat: 0.18, light: 0.24, spread: 5.4, spires: 1, spireH: 22, palm: false },
  acapulco: { rows: 11, hMin: 3, hMax: 9, hue: 0.11, sat: 0.05, light: 0.62, spread: 5.6, spires: 0, spireH: 0, palm: true, hill: true },
  new_orleans: { rows: 12, hMin: 3, hMax: 8, hue: 0.09, sat: 0.14, light: 0.3, spread: 5.4, spires: 0, spireH: 0, palm: false, levee: true },
};

// Procedural lit-window texture reused across all buildings (efficient, loads instantly)
function makeWindowTexture() {
  const c = document.createElement("canvas");
  c.width = 64; c.height = 128;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#070b13"; ctx.fillRect(0, 0, 64, 128);
  for (let y = 6; y < 124; y += 11) {
    for (let x = 6; x < 58; x += 13) {
      const lit = Math.random() > 0.42;
      ctx.fillStyle = lit ? (Math.random() > 0.5 ? "#ffd98a" : "#bfe0ff") : "#12203a";
      ctx.fillRect(x, y, 8, 7);
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(2, 5);
  return t;
}

// Street lamps lining both sidewalks; warm glow + point light at night
function StreetLights({ night, count = 8 }) {
  const arr = useMemo(() => new Array(count).fill(0).map((_, i) => ({ side: i % 2 ? -1 : 1, z: -Math.floor(i / 2) * 9 - 6 })), [count]);
  return (
    <group>
      {arr.map((p, i) => {
        const x = p.side * 4.1;
        const armX = p.side > 0 ? -0.6 : 0.6;
        return (
          <group key={i} position={[x, 0, p.z]}>
            <mesh position={[0, 2.3, 0]}><cylinderGeometry args={[0.05, 0.08, 4.6, 8]} /><meshStandardMaterial color="#2a2e38" metalness={0.6} roughness={0.5} /></mesh>
            <mesh position={[armX / 2, 4.5, 0]}><boxGeometry args={[Math.abs(armX), 0.08, 0.14]} /><meshStandardMaterial color="#2a2e38" /></mesh>
            <mesh position={[armX, 4.4, 0]}><sphereGeometry args={[0.16, 10, 10]} /><meshBasicMaterial color={night ? "#ffe0a0" : "#6a6a72"} toneMapped={false} /></mesh>
            {night && (
              <group position={[armX, 4.4, 0]}>
                {/* soft additive halo glow around the bulb */}
                <mesh><sphereGeometry args={[0.34, 16, 16]} /><meshBasicMaterial color="#ffdca0" transparent opacity={0.5} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} /></mesh>
                <mesh><sphereGeometry args={[0.72, 16, 16]} /><meshBasicMaterial color="#ffcf87" transparent opacity={0.2} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} /></mesh>
                {/* volumetric downward beam the rain streaks pass through */}
                <mesh position={[0, -2.1, 0]}><coneGeometry args={[1.5, 4.2, 20, 1, true]} /><meshBasicMaterial color="#ffce7a" transparent opacity={0.07} side={THREE.DoubleSide} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} /></mesh>
                <pointLight position={[0, -0.2, 0]} intensity={1.8} distance={10} color="#ffce7a" />
              </group>
            )}
          </group>
        );
      })}
    </group>
  );
}

// Cars: parked along both curbs (no driving — avoids the "driving under water" look during surge)
function Cars({ night, count = 7 }) {
  const cars = useMemo(() => new Array(count).fill(0).map((_, i) => ({
    x: (i % 2 ? -3.0 : 3.0), z: -Math.floor(i / 2) * 8 - 5,
    color: new THREE.Color().setHSL(Math.random(), 0.45, 0.45 + Math.random() * 0.15),
  })), [count]);
  return (
    <group>
      {cars.map((c, i) => (
        <group key={i} position={[c.x, 0.05, c.z]}>
          <mesh position={[0, 0.28, 0]}><boxGeometry args={[1.1, 0.46, 2.4]} /><meshStandardMaterial color={c.color} metalness={0.65} roughness={0.32} /></mesh>
          <mesh position={[0, 0.62, -0.1]}><boxGeometry args={[0.98, 0.42, 1.2]} /><meshStandardMaterial color={c.color} metalness={0.65} roughness={0.32} /></mesh>
          {night && (
            <>
              <mesh position={[0.34, 0.3, -1.22]}><sphereGeometry args={[0.08, 8, 8]} /><meshBasicMaterial color="#fff3c0" toneMapped={false} /></mesh>
              <mesh position={[-0.34, 0.3, -1.22]}><sphereGeometry args={[0.08, 8, 8]} /><meshBasicMaterial color="#fff3c0" toneMapped={false} /></mesh>
              <mesh position={[0, 0.32, 1.22]}><boxGeometry args={[0.9, 0.12, 0.05]} /><meshBasicMaterial color="#ff2a1a" toneMapped={false} /></mesh>
            </>
          )}
        </group>
      ))}
    </group>
  );
}

// Low residential houses (pitched roofs) for the low-rise coastal cities
function Houses({ count = 6, night, sim }) {
  const refs = useRef([]);
  const arr = useMemo(() => new Array(count).fill(0).map((_, i) => ({
    x: (i % 2 ? -6.6 : 6.6), z: -i * 8 - 6,
    c: new THREE.Color().setHSL(0.09 + Math.random() * 0.05, 0.2, 0.5 + Math.random() * 0.12),
    roof: new THREE.Color().setHSL(0.02, 0.4, 0.28),
  })), [count]);
  useFrame((s) => {
    const it = sim ? sim.current.intensity / 5 : 0;
    const shake = it > 0.85 ? (it - 0.85) * 0.14 : 0;   // structural vibration under Cat 5 stress
    refs.current.forEach((r, i) => { if (r) r.position.x = arr[i].x + Math.sin(s.clock.elapsedTime * 34 + i * 2) * shake; });
  });
  return (
    <group>
      {arr.map((h, i) => (
        <group key={i} ref={(el) => (refs.current[i] = el)} position={[h.x, 0, h.z]}>
          <mesh position={[0, 1.1, 0]}><boxGeometry args={[3.2, 2.2, 3.4]} /><meshStandardMaterial color={h.c} roughness={0.9} emissive={night ? "#ffcf87" : "#000"} emissiveIntensity={night ? 0.25 : 0} /></mesh>
          <mesh position={[0, 2.6, 0]} rotation={[0, Math.PI / 4, 0]}><coneGeometry args={[2.5, 1.2, 4]} /><meshStandardMaterial color={h.roof} roughness={1} /></mesh>
        </group>
      ))}
    </group>
  );
}

function Buildings({ sim, night, cityId, windowTex }) {
  const group = useRef();
  const cfg = CITY_LAYOUTS[cityId] || CITY_LAYOUTS.nyc;
  const blds = useMemo(() => {
    const arr = [];
    for (let side = -1; side <= 1; side += 2) {
      for (let i = 0; i < cfg.rows; i++) {
        const isSpire = cfg.spires > 0 && i < cfg.spires && side === -1;
        const h = isSpire ? cfg.spireH : cfg.hMin + Math.random() * (cfg.hMax - cfg.hMin);
        arr.push({
          x: side * (5.8 + Math.random() * 1.8), z: -i * 5 - 2,
          w: 2.6 + Math.random() * 1.6, h, d: 2.6 + Math.random() * 1.4,
          c: new THREE.Color().setHSL(0.095, 0.16, 0.70 + Math.random() * 0.12),
          glass: isSpire || Math.random() > 0.5,
          seed: Math.random() * 10, spire: isSpire,
        });
      }
    }
    return arr;
  }, [cfg]);

  useFrame((s) => {
    const shake = (sim.current.intensity / 5) ** 2 * 0.05;
    if (!group.current) return;
    group.current.children.forEach((m, i) => {
      const b = blds[i];
      if (b) m.position.x = b.x + Math.sin(s.clock.elapsedTime * 30 + b.seed) * shake;
    });
  });

  return (
    <group ref={group}>
      {blds.map((b, i) => (
        <mesh key={i} position={[b.x, b.h / 2, b.z]}>
          <boxGeometry args={[b.w, b.h, b.d]} />
          {b.glass ? (
            <meshStandardMaterial color="#43566f" roughness={0.14} metalness={0.85}
              emissiveMap={windowTex} emissive={new THREE.Color(night ? "#bfe0ff" : "#9fc0e0")} emissiveIntensity={night ? 1.1 : 0.5} />
          ) : (
            <meshStandardMaterial color={b.c} roughness={0.9} metalness={0.05}
              emissiveMap={windowTex} emissive={new THREE.Color(night ? "#ffe1a0" : "#c9bfa4")} emissiveIntensity={night ? 0.9 : 0.16} />
          )}
        </mesh>
      ))}
      {cfg.hill && (
        <mesh position={[10, 5, -30]} rotation={[0, 0.4, 0]}>
          <boxGeometry args={[18, 12, 40]} />
          <meshStandardMaterial color="#2c3a26" roughness={1} />
        </mesh>
      )}
      {cfg.levee && (
        <mesh position={[0, 0.8, 8]} rotation={[-0.15, 0, 0]}>
          <boxGeometry args={[9, 1.6, 3]} />
          <meshStandardMaterial color="#3a3228" roughness={1} />
        </mesh>
      )}
    </group>
  );
}

function Palms({ sim, count = 8 }) {
  const refs = useRef([]);
  const pos = useMemo(() => new Array(count).fill(0).map((_, i) => [i % 2 ? -3.2 : 3.2, 0, -i * 6 - 4]), [count]);
  useFrame((s) => {
    const wind = (sim.current.intensity / 5) * 0.6;
    refs.current.forEach((r, i) => { if (r) r.rotation.z = Math.sin(s.clock.elapsedTime * 4 + i) * wind; });
  });
  return (
    <group>
      {pos.map((p, i) => (
        <group key={i} position={p}>
          <mesh position={[0, 1.5, 0]}><cylinderGeometry args={[0.08, 0.14, 3, 6]} /><meshStandardMaterial color="#5a4326" /></mesh>
          <group ref={(el) => (refs.current[i] = el)} position={[0, 3, 0]}>
            {[0, 1, 2, 3, 4].map((k) => (
              <mesh key={k} rotation={[0.5, (k / 5) * Math.PI * 2, 0]} position={[0, 0, 0]}>
                <coneGeometry args={[0.12, 1.6, 4]} /><meshStandardMaterial color="#2f6b3a" side={THREE.DoubleSide} />
              </mesh>
            ))}
          </group>
        </group>
      ))}
    </group>
  );
}

function Tree({ position, sim }) {
  const foliage = useRef();
  const branch = useRef();
  const seed = useMemo(() => Math.random() * 10, []);
  useFrame((s) => {
    const it = sim.current.intensity / 5;
    const t = s.clock.elapsedTime;
    if (foliage.current) {
      const lean = it * 0.9;                                        // baseline bend into the wind (-x)
      const whip = Math.sin(t * (6 + it * 12) + seed) * it * (0.25 + it * 0.65);  // violent whip at Cat 5
      foliage.current.rotation.z = -(lean + whip);
      foliage.current.rotation.x = Math.cos(t * 4 + seed) * it * 0.4;
    }
    if (branch.current) {
      const broken = it > 0.9;                                      // a branch snaps at Cat 5
      branch.current.visible = broken;
      if (broken) branch.current.rotation.z = -1.15 + Math.sin(t * 22 + seed) * 0.28;
    }
  });
  return (
    <group position={position}>
      <mesh position={[0, 0.8, 0]}><cylinderGeometry args={[0.08, 0.12, 1.6, 6]} /><meshStandardMaterial color="#3b2b1a" /></mesh>
      <group ref={foliage} position={[0, 1.6, 0]}>
        <mesh position={[0, 0.4, 0]}><coneGeometry args={[0.7, 1.4, 8]} /><meshStandardMaterial color="#1f5132" /></mesh>
        <group ref={branch} position={[0.2, 0.2, 0]} visible={false}>
          <mesh position={[0.3, 0, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.03, 0.04, 0.6, 5]} /><meshStandardMaterial color="#3b2b1a" /></mesh>
          <mesh position={[0.6, 0, 0]}><coneGeometry args={[0.24, 0.5, 6]} /><meshStandardMaterial color="#1f5132" /></mesh>
        </group>
      </group>
    </group>
  );
}

function RainLines({ sim, count, night = false }) {
  const ref = useRef();
  const heads = useMemo(() => {
    const a = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) { a[i * 3] = (Math.random() - 0.5) * 44; a[i * 3 + 1] = Math.random() * 30; a[i * 3 + 2] = (Math.random() - 0.5) * 70 - 20; }
    return a;
  }, [count]);
  const positions = useMemo(() => new Float32Array(count * 6), [count]);
  useFrame((s, dt) => {
    const geo = ref.current?.geometry; if (!geo) return;
    const intensity = sim.current.intensity / 5;
    const active = Math.floor(count * (0.15 + 0.85 * intensity));
    const windX = 2 + intensity * 34, fall = 30 + intensity * 20, len = 0.9 + intensity * 0.6;
    const dir = new THREE.Vector3(windX, -fall, 0).normalize();
    const arr = geo.attributes.position.array;
    for (let i = 0; i < count; i++) {
      if (i < active) {
        heads[i * 3] -= windX * dt; heads[i * 3 + 1] -= fall * dt;
        if (heads[i * 3 + 1] < 0 || heads[i * 3] < -24) { heads[i * 3] = (Math.random() - 0.5) * 44 + 12; heads[i * 3 + 1] = 28 + Math.random() * 4; }
        const hx = heads[i * 3], hy = heads[i * 3 + 1], hz = heads[i * 3 + 2];
        arr[i * 6] = hx; arr[i * 6 + 1] = hy; arr[i * 6 + 2] = hz;
        arr[i * 6 + 3] = hx - dir.x * len; arr[i * 6 + 4] = hy - dir.y * len; arr[i * 6 + 5] = hz;
      } else { for (let k = 0; k < 6; k++) arr[i * 6 + k] = 0; }
    }
    geo.attributes.position.needsUpdate = true;
    // at night the streaks glow (additive), catching the warm streetlight
    if (ref.current) ref.current.material.opacity = Math.min(1, (0.2 + intensity * 0.55) * (night ? 1.3 : 1));
  });
  return (
    <lineSegments ref={ref}>
      <bufferGeometry><bufferAttribute attach="attributes-position" array={positions} count={count * 2} itemSize={3} /></bufferGeometry>
      <lineBasicMaterial color={night ? "#ffe0a6" : "#a9c7e8"} transparent opacity={0.4}
        toneMapped={!night} blending={night ? THREE.AdditiveBlending : THREE.NormalBlending} />
    </lineSegments>
  );
}

function Snow({ sim, count }) {
  const ref = useRef();
  const pos = useMemo(() => {
    const a = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) { a[i * 3] = (Math.random() - 0.5) * 44; a[i * 3 + 1] = Math.random() * 30; a[i * 3 + 2] = (Math.random() - 0.5) * 70 - 20; }
    return a;
  }, [count]);
  useFrame((s, dt) => {
    const geo = ref.current?.geometry; if (!geo) return;
    const intensity = sim.current.intensity / 5;
    const arr = geo.attributes.position.array;
    const fall = 2.5 + intensity * 3, drift = 1 + intensity * 5;
    for (let i = 0; i < count; i++) {
      arr[i * 3 + 1] -= fall * dt; arr[i * 3] += Math.sin(s.clock.elapsedTime + i) * drift * dt;
      if (arr[i * 3 + 1] < 0) { arr[i * 3 + 1] = 28 + Math.random() * 4; arr[i * 3] = (Math.random() - 0.5) * 44; }
    }
    geo.attributes.position.needsUpdate = true;
    if (ref.current) ref.current.material.opacity = 0.4 + intensity * 0.5;
  });
  return (
    <points ref={ref}>
      <bufferGeometry><bufferAttribute attach="attributes-position" array={pos} count={count} itemSize={3} /></bufferGeometry>
      <pointsMaterial color="#ffffff" size={0.14} transparent opacity={0.6} depthWrite={false} />
    </points>
  );
}

function Surge({ sim, maxLevel, waterRef }) {
  const mat = useRef(); const mesh = useRef();
  const uniforms = useMemo(() => ({ uTime: { value: 0 }, uColor: { value: new THREE.Color("#12496b") } }), []);
  useFrame((s) => {
    const intensity = sim.current.intensity / 5;
    if (mat.current) mat.current.uniforms.uTime.value = s.clock.elapsedTime;
    const y = -0.4 + intensity * maxLevel;
    if (mesh.current) mesh.current.position.y = y;
    waterRef.current = y;
  });
  return (
    <mesh ref={mesh} rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.4, -30]}>
      <planeGeometry args={[90, 130, 60, 90]} />
      <shaderMaterial ref={mat} vertexShader={surgeVert} fragmentShader={surgeFrag} uniforms={uniforms} transparent />
    </mesh>
  );
}

// Flying debris — InstancedMesh (90+ FPS), density + speed scale with wind, drifts with the rain
function Debris({ sim, count }) {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const bits = useMemo(() => new Array(count).fill(0).map(() => ({
    x: (Math.random() - 0.5) * 24, y: 0.5 + Math.random() * 7, z: -Math.random() * 55 - 2,
    s: 0.08 + Math.random() * 0.28, rx: Math.random() * 6, rz: Math.random() * 6, seed: Math.random() * 10,
  })), [count]);
  useFrame((s, dt) => {
    if (!mesh.current) return;
    const intensity = sim.current.intensity / 5;
    const active = Math.floor(count * (0.1 + 0.9 * intensity));   // density scales with wind velocity
    const spd = 8 + intensity * 42;                                // speed scales with wind velocity
    const t = s.clock.elapsedTime;
    for (let i = 0; i < count; i++) {
      const b = bits[i];
      if (i < active && intensity > 0.2) {
        b.x -= spd * dt;                                           // same direction as the rain (-x)
        b.z += Math.sin(t * 1.5 + b.seed) * intensity * 6 * dt;    // rotational swirl of the storm flow
        if (b.x < -14) { b.x = 14; b.z = -Math.random() * 55 - 2; b.y = 0.5 + Math.random() * 7; }
        b.rx += dt * (4 + intensity * 9); b.rz += dt * (3 + intensity * 8);
        dummy.position.set(b.x, b.y + Math.sin(t * 5 + b.seed) * intensity * 1.4, b.z);
        dummy.rotation.set(b.rx, 0, b.rz);
        dummy.scale.setScalar(b.s);
      } else {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0.0001);
      }
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
  });
  return (
    <instancedMesh ref={mesh} args={[null, null, count]}>
      <planeGeometry args={[0.34, 0.2]} />
      <meshStandardMaterial color="#8a7448" side={THREE.DoubleSide} roughness={0.9} />
    </instancedMesh>
  );
}

function FirstPersonRig({ moveRef, waterRef, enabled }) {
  const { camera, gl } = useThree();
  const state = useRef({ x: 0, z: 6, yaw: 0, pitch: -0.03 });
  const drag = useRef({ active: false, x: 0, y: 0 });
  useEffect(() => {
    if (!enabled) return;
    camera.position.set(state.current.x, 1.7, state.current.z);
    const onKey = (e, down) => {
      const m = moveRef.current; const k = e.key.toLowerCase();
      if (k === "w" || e.key === "ArrowUp") m.f = down;
      if (k === "s" || e.key === "ArrowDown") m.b = down;
      if (k === "a" || e.key === "ArrowLeft") m.l = down;
      if (k === "d" || e.key === "ArrowRight") m.r = down;
      if (k === "q") m.tl = down; if (k === "e") m.tr = down;
    };
    const kd = (e) => onKey(e, true); const ku = (e) => onKey(e, false);
    // Click-and-drag 360° mouse look (no pointer-lock — safe on Quest & Vision Pro browsers)
    const el = gl.domElement;
    const down = (e) => { drag.current.active = true; drag.current.x = e.clientX; drag.current.y = e.clientY; el.style.cursor = "grabbing"; };
    const move = (e) => {
      if (!drag.current.active) return;
      const dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y;
      drag.current.x = e.clientX; drag.current.y = e.clientY;
      state.current.yaw -= dx * 0.005;
      state.current.pitch = THREE.MathUtils.clamp(state.current.pitch - dy * 0.005, -1.2, 1.2);
    };
    const up = () => { drag.current.active = false; el.style.cursor = "grab"; };
    el.style.cursor = "grab";
    el.addEventListener("pointerdown", down);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => {
      el.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku);
      el.style.cursor = "auto";
    };
  }, [camera, moveRef, enabled, gl]);
  useFrame((s, dt) => {
    if (!enabled) return;
    const m = moveRef.current; const st = state.current; const turn = 1.4 * dt;
    if (m.tl) st.yaw += turn; if (m.tr) st.yaw -= turn;
    const spd = 6 * dt; const fx = Math.sin(st.yaw), fz = Math.cos(st.yaw);
    if (m.f) { st.x -= fx * spd; st.z -= fz * spd; }
    if (m.b) { st.x += fx * spd; st.z += fz * spd; }
    if (m.l) { st.x -= fz * spd; st.z += fx * spd; }
    if (m.r) { st.x += fz * spd; st.z -= fx * spd; }
    st.z = THREE.MathUtils.clamp(st.z, -60, 12);
    st.x = THREE.MathUtils.clamp(st.x, -3.4, 3.4);           // wall collision
    const eyeY = Math.max(1.7, (waterRef.current || -0.4) + 0.6);  // never underwater
    camera.position.set(st.x, eyeY, st.z);
    const cp = Math.cos(st.pitch);
    camera.lookAt(st.x - fx * 3 * cp, eyeY + Math.sin(st.pitch) * 3, st.z - fz * 3 * cp);
  });
  return null;
}

// Reference-matched boulevard: asphalt carriageways, painted lanes, concrete planted median + sidewalks
function Boulevard() {
  const dashZ = useMemo(() => new Array(22).fill(0).map((_, i) => -i * 5 - 2), []);
  return (
    <group>
      {/* central concrete median planter (grass) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.07, -25]}><planeGeometry args={[1.8, 130]} /><meshStandardMaterial color="#3f6b34" roughness={1} /></mesh>
      {[-0.95, 0.95].map((x, i) => (<mesh key={`curb${i}`} position={[x, 0.09, -25]}><boxGeometry args={[0.14, 0.22, 130]} /><meshStandardMaterial color="#a7adb4" roughness={0.9} /></mesh>))}
      {/* dashed lane markings on each carriageway */}
      {dashZ.map((z, i) => (<mesh key={`dl${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[-2.1, 0.061, z]}><planeGeometry args={[0.14, 2.4]} /><meshBasicMaterial color="#e8e3cf" /></mesh>))}
      {dashZ.map((z, i) => (<mesh key={`dr${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[2.1, 0.061, z]}><planeGeometry args={[0.14, 2.4]} /><meshBasicMaterial color="#e8e3cf" /></mesh>))}
      {/* solid edge lines */}
      {[-3.5, 3.5].map((x, i) => (<mesh key={`el${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.061, -25]}><planeGeometry args={[0.1, 130]} /><meshBasicMaterial color="#d8d2bd" /></mesh>))}
      {/* concrete sidewalks */}
      {[-4.6, 4.6].map((x, i) => (<mesh key={`sw${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.05, -25]}><planeGeometry args={[2.2, 130]} /><meshStandardMaterial color="#b8b2a2" roughness={0.95} /></mesh>))}
      {/* crosswalks, stop lines & street signs at two intersections */}
      {[-9, -42].map((cz, k) => (
        <group key={`x${k}`}>
          {new Array(9).fill(0).map((_, j) => (<mesh key={j} rotation={[-Math.PI / 2, 0, 0]} position={[-3.2 + j * 0.8, 0.063, cz]}><planeGeometry args={[0.42, 2.2]} /><meshBasicMaterial color="#e9e4d0" /></mesh>))}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-2.1, 0.063, cz + 1.7]}><planeGeometry args={[2.6, 0.24]} /><meshBasicMaterial color="#e9e4d0" /></mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[2.1, 0.063, cz - 1.7]}><planeGeometry args={[2.6, 0.24]} /><meshBasicMaterial color="#e9e4d0" /></mesh>
          <group position={[-4.4, 0, cz]}>
            <mesh position={[0, 1.1, 0]}><cylinderGeometry args={[0.04, 0.04, 2.2, 6]} /><meshStandardMaterial color="#5a5f66" metalness={0.5} /></mesh>
            <mesh position={[0, 2.15, 0]}><cylinderGeometry args={[0.28, 0.28, 0.05, 8]} /><meshStandardMaterial color="#b3261e" emissive="#5a0f0a" emissiveIntensity={0.25} /></mesh>
          </group>
          <group position={[4.4, 0, cz]}>
            <mesh position={[0, 1.3, 0]}><cylinderGeometry args={[0.04, 0.04, 2.6, 6]} /><meshStandardMaterial color="#5a5f66" metalness={0.5} /></mesh>
            <mesh position={[0.35, 2.35, 0]}><boxGeometry args={[0.95, 0.3, 0.04]} /><meshStandardMaterial color="#1f6b3a" emissive="#0c2c18" emissiveIntensity={0.18} /></mesh>
          </group>
        </group>
      ))}
    </group>
  );
}

// Cool night moon: glowing disc + halo high in the sky (replaces the sun flare at night)
function MoonGlow({ preset }) {
  if (!preset.night) return null;
  return (
    <group position={[-14, 24, -54]}>
      <mesh><sphereGeometry args={[2.3, 24, 24]} /><meshBasicMaterial color="#eaf0ff" toneMapped={false} /></mesh>
      <mesh><sphereGeometry args={[4.6, 24, 24]} /><meshBasicMaterial color="#9fb6ff" transparent opacity={0.26} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} /></mesh>
      <mesh><sphereGeometry args={[9.5, 24, 24]} /><meshBasicMaterial color="#6f8bff" transparent opacity={0.12} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} /></mesh>
      <pointLight intensity={0.6} distance={220} color="#bcd0ff" />
    </group>
  );
}

// Brilliant morning solar flare high in the sky (matches the reference photos)
function SunFlare({ preset }) {
  if (preset.night) return null;
  const warm = preset.label === "Morning";
  return (
    <group position={[2.5, 27, -56]}>
      <mesh><sphereGeometry args={[2.6, 24, 24]} /><meshBasicMaterial color={warm ? "#fff6d8" : "#fff9ec"} toneMapped={false} /></mesh>
      <mesh><sphereGeometry args={[6.5, 24, 24]} /><meshBasicMaterial color={warm ? "#ffeeb4" : "#ffffff"} transparent opacity={0.3} depthWrite={false} toneMapped={false} /></mesh>
      <mesh><sphereGeometry args={[12, 24, 24]} /><meshBasicMaterial color={warm ? "#ffe9a6" : "#eaf2ff"} transparent opacity={0.12} depthWrite={false} toneMapped={false} blending={THREE.AdditiveBlending} /></mesh>
      <pointLight intensity={0.7} distance={240} color="#fff2d0" />
    </group>
  );
}

// Nor'easter frontal boundaries sweeping down the boulevard as the timeline advances
function FrontalSweep({ timeline }) {
  const coldZ = 14 - timeline * 92;      // leading blue cold front marches south (-Z)
  const warmZ = coldZ + 10;              // trailing red warm front
  return (
    <group data-testid="frontal-sweep">
      {/* warm sector tint between the two fronts */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.066, (coldZ + warmZ) / 2]}>
        <planeGeometry args={[11, Math.abs(warmZ - coldZ)]} />
        <meshBasicMaterial color="#7a3b3b" transparent opacity={0.16} depthWrite={false} />
      </mesh>
      {/* COLD FRONT — blue */}
      <group position={[0, 0, coldZ]} data-testid="cold-front">
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.07, 0]}><planeGeometry args={[11, 0.6]} /><meshBasicMaterial color="#3f7bff" transparent opacity={0.9} depthWrite={false} /></mesh>
        <mesh position={[0, 3.2, 0]}><planeGeometry args={[11, 6.4]} /><meshBasicMaterial color="#4d8bff" transparent opacity={0.14} side={THREE.DoubleSide} depthWrite={false} /></mesh>
      </group>
      {/* WARM FRONT — red */}
      <group position={[0, 0, warmZ]} data-testid="warm-front">
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.068, 0]}><planeGeometry args={[11, 0.5]} /><meshBasicMaterial color="#ff5a5a" transparent opacity={0.85} depthWrite={false} /></mesh>
        <mesh position={[0, 2.6, 0]}><planeGeometry args={[11, 5.2]} /><meshBasicMaterial color="#ff6b6b" transparent opacity={0.12} side={THREE.DoubleSide} depthWrite={false} /></mesh>
      </group>
    </group>
  );
}

export default function CityScene({ sim, city, event, precipSnow, moveRef, preset, cycle, cityCam, onTeleport, profile, timeline = 0, reducedMotion = false }) {
  const { scene } = useThree();
  const waterRef = useRef(-0.4);
  const groundRef = useRef();
  const cityId = city?.id || "nyc";
  const maxLevel = event ? Math.min(6, event.surge_ft / 6) : 3;
  const orbit = cityCam === "orbit";
  const inVR = useXR((s) => !!s.session);
  const p = inVR ? PROFILES.vr : (profile || PROFILES.desktop);
  const quality = p.cityMult * (reducedMotion ? 0.6 : 1);
  const rainCount = Math.round(p.rain * (reducedMotion ? 0.6 : 1));
  const snowCount = Math.round(p.snow * (reducedMotion ? 0.6 : 1));
  const debrisCount = Math.round(p.debris);
  const cfg = CITY_LAYOUTS[cityId] || CITY_LAYOUTS.nyc;
  const windowTex = useMemo(makeWindowTexture, []);
  const lowRise = !(cfg.spires > 0);
  const lightCount = Math.max(6, Math.round(10 * quality));
  const carCount = Math.max(4, Math.round(7 * quality));

  useEffect(() => {
    scene.background = new THREE.Color(preset.skyBottom);
    scene.fog = new THREE.Fog(new THREE.Color(preset.fog), 6, 60);
    return () => { scene.background = new THREE.Color("#02030a"); scene.fog = null; };
  }, [scene, preset]);

  useFrame(() => {
    const intensity = sim.current.intensity / 5;
    const sky = new THREE.Color(preset.skyBottom).lerp(new THREE.Color("#0a0d12"), intensity * 0.7);
    if (scene.background) scene.background.copy(sky);
    if (scene.fog) scene.fog.color.copy(new THREE.Color(preset.fog).lerp(new THREE.Color("#05070c"), intensity * 0.6));
    // wet asphalt reflection: roughness drops as it rains harder
    if (groundRef.current) { groundRef.current.roughness = 0.65 - intensity * 0.5; groundRef.current.metalness = 0.2 + intensity * 0.6; }
  });

  const dirColor = new THREE.Color(preset.dirColor).lerp(new THREE.Color(cycle.tintLight), 0.5);
  const ambColor = new THREE.Color(preset.ambientColor).lerp(new THREE.Color(cycle.tintAmbient), 0.5);

  return (
    <group>
      <ambientLight intensity={0.7} color={ambColor} />
      <directionalLight position={[6, 12, 4]} intensity={preset.dirIntensity} color={dirColor} />
      <hemisphereLight args={["#33405c", "#05070c", 0.4]} />

      {orbit ? (
        <OrbitControls makeDefault target={[0, 1.6, -12]} enablePan={false} enableDamping maxDistance={45} minDistance={1.5} maxPolarAngle={Math.PI / 2 - 0.02} />
      ) : (
        <FirstPersonRig moveRef={moveRef} waterRef={waterRef} enabled={!orbit} />
      )}

      <TeleportTarget onTeleport={(v) => onTeleport && onTeleport([v.x, v.y, v.z])}>
        <mesh ref={undefined} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -25]} receiveShadow>
          <planeGeometry args={[11, 130]} />
          <meshStandardMaterial ref={groundRef} color="#14161c" roughness={0.6} metalness={0.2} />
        </mesh>
      </TeleportTarget>

      <Boulevard />
      {cycle.id === "noreaster" && <FrontalSweep timeline={timeline} />}
      <SunFlare preset={preset} />
      <MoonGlow preset={preset} />

      <Buildings sim={sim} night={preset.night} cityId={cityId} windowTex={windowTex} />
      <StreetLights night={preset.night} count={lightCount} />
      <Cars night={preset.night} count={carCount} />
      {lowRise && <Houses night={preset.night} count={Math.max(4, Math.round(6 * quality))} sim={sim} />}
      {cfg.palm ? <Palms sim={sim} count={Math.max(4, Math.round(8 * quality))} /> : (
        <>
          {new Array(Math.round(9 * quality)).fill(0).map((_, i) => (<Tree key={`m${i}`} position={[0, 0.14, -i * 6 - 4]} sim={sim} />))}
          {new Array(Math.round(6 * quality)).fill(0).map((_, i) => (<Tree key={`s${i}`} position={[i % 2 ? -4.6 : 4.6, 0.05, -i * 7 - 6]} sim={sim} />))}
        </>
      )}

      {precipSnow ? <Snow sim={sim} count={snowCount} /> : <RainLines sim={sim} count={rainCount} night={preset.night} />}
      <Debris sim={sim} count={debrisCount} />
      <Surge sim={sim} maxLevel={maxLevel} waterRef={waterRef} />

      <Suspense fallback={null}>
        <Billboard position={[0, 6, -8]}>
          <Text font="/fonts/Cinzel.ttf" fontSize={0.6} color={GOLD} anchorX="center" outlineWidth={0.01} outlineColor="#000">
            {city ? city.name.toUpperCase() : ""}
          </Text>
        </Billboard>
      </Suspense>
    </group>
  );
}
